import React, { useEffect } from "react";

interface DateFilterProps {
    value?: string;
    onChange?: (date: string) => void;
    disabled?: boolean;
    maxDate?: string;
    filterField?: string;
    name?: string;
    formData?: Record<string, any>;
    onFormDataChange?: (name: string, value: any) => void;
    config?: any;
}

const DateFilter: React.FC<DateFilterProps> = ({
    value,
    onChange,
    disabled = false,
    maxDate,
    filterField = "date",
    name,
    formData,
    onFormDataChange,
    config,
}) => {
    const getTodayDate = () => new Date().toISOString().split("T")[0];

    // Get value from formData if name is provided, otherwise use value prop
    const fieldName = name || config?.name;
    const currentValue = fieldName && formData ? formData[fieldName] : value;
    const configDefaultValue = config?.defaultValue ?? config?.props?.defaultValue;
    const configMaxDate = config?.maxDate ?? config?.props?.maxDate;
    const defaultDate = configDefaultValue === "today" ? getTodayDate() : configDefaultValue;
    const resolvedValue = currentValue || defaultDate || "";
    const resolvedMaxDate =
        maxDate === "today" || configMaxDate === "today" ? getTodayDate() : (maxDate || configMaxDate);

    useEffect(() => {
        if (!fieldName || !onFormDataChange) return;
        if (currentValue) return;
        if (!defaultDate) return;
        onFormDataChange(fieldName, defaultDate);
    }, [currentValue, defaultDate, fieldName, onFormDataChange]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        // If onFormDataChange is provided and we have a field name, use it
        if (onFormDataChange && fieldName) {
            onFormDataChange(fieldName, newValue);
        }

        // Also call onChange if provided (for backward compatibility)
        if (onChange) {
            onChange(newValue);
        }
    };

    const compact = Boolean(config?.compact || config?.style?.compact);

    return (
        <div className={`flex items-center ${compact ? "gap-2" : "gap-2"}`}>
            {!compact && (
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Filter by {filterField}:
                </label>
            )}
            <input
                type="date"
                value={resolvedValue}
                onChange={handleChange}
                disabled={disabled || config?.disabled}
                max={resolvedMaxDate}
                aria-label={`Filter by ${filterField}`}
                className={
                    compact
                        ? "h-9 px-3 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                        : "px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                }
            />
        </div>
    );
};

export default DateFilter;
